<script setup lang="ts">
import type {
  Child,
  PublicSchool,
  PublicSchoolCity,
} from 'src/modules/Child/models/Child';
import { mdiAccountEdit, mdiAccountPlus } from '@quasar/extras/mdi-v7';
import useValidators from 'src/shared/composables/useValidators';
import { QForm } from 'quasar';
import BAdminInput from 'src/shared/components/base/BAdminInput/BAdminInput.vue';
import BAdminSelect from 'src/shared/components/base/BAdminSelect/BAdminSelect.vue';
import BCard from 'src/shared/components/base/BCard/BCard.vue';
import { useI18n } from 'vue-i18n';
import { useSelectOptions } from 'src/shared/composables/strapi/useSelectOptions';
import { ref, computed, watch } from 'vue';

interface Props {
  isEditing: boolean;
}

defineProps<Props>();

const { t } = useI18n();

const { required, min, max, namePattern } = useValidators();

// City/school selects bind to option objects (so an existing selection renders
// when editing); the shared `required` rule only accepts strings/numbers, so use
// a truthiness check for these object-valued selects.
const requiredSelection = (val: unknown) => !!val || t('PLEASE_ENTER_A_VALUE');

// The parent only renders this card inside `v-if="currentChild"`, so the model
// is always provided. Declaring it required keeps `Child` fully inferred in the
// template (a partial default silently widened it to `unknown`).
const childFormData = defineModel<Child>('child', { required: true });

// City + school are driven by the public-school relation. We bind the selects
// to full option objects (no emit-value) so an existing selection still renders
// when editing, even before the async options list has loaded.
const selectedCity = ref<PublicSchoolCity | null>(
  childFormData.value?.public_school?.city ?? null,
);
const selectedSchool = ref<PublicSchool | null>(
  childFormData.value?.public_school ?? null,
);

const {
  options: cities,
  search: citySearch,
  loading: cityLoading,
} = useSelectOptions('cities', 'city', {});

// School selection (filtered by city)
const schoolFilters = computed(() =>
  selectedCity.value?.id ? { city: selectedCity.value.id } : {},
);

const {
  options: schools,
  search: schoolSearch,
  loading: schoolLoading,
} = useSelectOptions('public-schools', 'short_name', schoolFilters);

// Reset the school selection when the city changes.
watch(selectedCity, (next, prev) => {
  if (prev !== undefined && next?.id !== prev?.id) {
    selectedSchool.value = null;
  }
});

// Mirror the chosen public-school onto the form model: keep the relation object
// (for optimistic display) and the legacy `school` string as a fallback.
watch(selectedSchool, (school) => {
  childFormData.value.public_school = school
    ? {
        id: school.id,
        documentId: school.documentId,
        short_name: school.short_name,
        full_name: school.full_name,
        city: selectedCity.value
          ? { id: selectedCity.value.id, city: selectedCity.value.city }
          : null,
      }
    : null;
  childFormData.value.school = school?.short_name || school?.full_name || '';
});

const emits = defineEmits<{
  saveChild: [];
}>();
</script>

<template>
  <BCard size="sm">
    <q-form @submit.prevent="emits('saveChild')">
      <q-card-section horizontal>
        <q-card-section>
          <div class="text-h6">
            {{ isEditing ? 'Edit Child' : 'Add Child' }}
          </div>
        </q-card-section>
        <q-space />
        <q-card-section>
          <q-icon
            size="24px"
            :name="isEditing ? mdiAccountEdit : mdiAccountPlus"
            color="accent"
          />
        </q-card-section>
      </q-card-section>

      <q-card-section class="q-gutter-md">
        <BAdminInput
          v-model="childFormData.firstname"
          :label="t('FIRST_NAME')"
          color="accent"
          autofocus
          :rules="[
            required,
            namePattern,
            (val: string) => min(val, 2),
            (val: string) => max(val, 32),
          ]"
        />
        <BAdminInput
          v-model="childFormData.lastname"
          :label="t('LAST_NAME')"
          color="accent"
          :rules="[
            required,
            namePattern,
            (val: string) => min(val, 2),
            (val: string) => max(val, 32),
          ]"
        />

        <BAdminSelect
          v-model="selectedCity"
          class="col-12 col-sm-5"
          :label="t('CITY')"
          :loading="cityLoading"
          :options="cities"
          option-label="city"
          :rules="[requiredSelection]"
          use-input
          input-debounce="0"
          @filter="
            (val: string, update: any) => update(() => (citySearch = val))
          "
        />

        <BAdminSelect
          v-model="selectedSchool"
          class="col-12 col-sm-5"
          :label="t('SCHOOL')"
          :loading="schoolLoading"
          :options="schools"
          option-label="short_name"
          :rules="[requiredSelection]"
          use-input
          input-debounce="0"
          :disable="!selectedCity"
          :hint="!selectedCity ? t('SELECT_CITY_FIRST') : ''"
          @filter="
            (val: string, update: any) => update(() => (schoolSearch = val))
          "
        />

        <BAdminInput
          v-model="childFormData.class"
          type="number"
          min="0"
          max="12"
          :label="t('CLASS')"
          color="accent"
          :rules="[required]"
        />
      </q-card-section>

      <q-card-actions align="right">
        <q-btn
          v-close-popup
          :label="t('CANCLE')"
          type="reset"
          flat
          :color="$q.dark.isActive ? 'white' : 'dark'"
        />
        <q-btn
          type="submit"
          :label="t('SAVE')"
          :color="$q.dark.isActive ? 'accent' : 'dark'"
          :text-color="$q.dark.isActive ? 'dark' : 'white'"
        />
      </q-card-actions>
    </q-form>
  </BCard>
</template>
